import { generateClient } from "@aws-amplify/api";
import { recordSentry } from "utils/recordSentry";
import { db } from "../db";
import { FieldContinuationData, FieldContinuationDataForResolver } from "../definitions";

/**
 * Given their ids, bulk grabs all the relevent fieldContinuationDatas
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids The ids of the fieldContinuationDatas we want to grab
 * @returns All fieldContinuationDataswe have with any of the provided ids
 */
export async function bulkGetFieldContinuationDatas(ids: string[]): Promise<FieldContinuationDataForResolver[]> {
	const retrievedFieldContinuationDatas = await db.fieldContinuationData.bulkGet(ids);
	const fieldContinuationDatas = retrievedFieldContinuationDatas.filter((data): data is FieldContinuationDataForResolver => data !== undefined);
	return fieldContinuationDatas;
}

/**
 * Gets all cont data from offline db by parentformtemplateid
 * @param parentFormTemplateId parentFormTemplateId or the one these belong to (same as their parnet field template)
 * @returns the contdatas
 */
export async function bulkGetFieldContinuationDatasByParentFormTemplateId(parentFormTemplateId: string): Promise<FieldContinuationDataForResolver[]> {
	const retrievedConDatas = await db.fieldContinuationData.where("parentFormTemplateId").equals(parentFormTemplateId).toArray();
	const fieldContinuationDatas = retrievedConDatas.filter((data): data is FieldContinuationDataForResolver => data !== undefined);
	return fieldContinuationDatas;
}

/**
 * Saves the provided list of continuation documents to the offline db.
 * @param continuationList list of the continuation data documents that need to be saved
 */
export async function bulkSaveFieldContinuationDatas(continuationList: FieldContinuationDataForResolver[]): Promise<void> {
	try {
		await db.fieldContinuationData.bulkPut(continuationList);
	} catch (error) {
		recordSentry({ error, msg: "Error saving continuation data to offline db: " });
	}
}

/**
 * Given a continuationData, removes any references and returns just the document itself
 * @author Eric Webb <ewebb@factorearth.com>
 * @param data The continuationData
 * @returns The parsed down continuation data
 */
export function createFieldContinuationDataForResolver(data: FieldContinuationData): FieldContinuationDataForResolver {
	const final: FieldContinuationDataForResolver = {
		id: data.id,
		createdAt: data.createdAt,
		updatedAt: data.updatedAt,
		group: data.group,
		readGroups: [...data.readGroups],
		continuationActions: data.continuationActions || [],
		fieldTemplateId: data.fieldTemplateId,
		formTemplateId: data.formTemplateId,
		instanceToContinueOn: data.instanceToContinueOn || "",
		parentFormTemplateId: data.parentFormTemplateId,
	};
	return final;
}

/**
 * Given 2 continuationDatas, looks at their updated at times and returns the one more recently updated. Creates a new object in the process to prevent bad references ("merge")
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donor The donor's continuationData
 * @param receiver The receiver's continuationData
 * @returns The "merged" continuation data
 */
export function createMergedFieldContinuationData(
	donor: FieldContinuationDataForResolver,
	receiver: FieldContinuationDataForResolver,
): FieldContinuationDataForResolver {
	// LISTEN UP: To be in here, you should already have the correct fieldTemplate selected, and by that, get the continuationDatas from it. This should only be used in the event that both
	// parties have the continuationData with the same id, and we just want to see if the updatedAt time is different so we can take that version
	const timeStamp = new Date();
	const donorUpdated = new Date(donor.updatedAt);
	const receiverUpdated = new Date(receiver.updatedAt);
	const evaluation = donorUpdated > receiverUpdated;
	const merged: FieldContinuationDataForResolver = {
		id: donor.id || receiver.id,
		continuationActions: evaluation ? [...donor.continuationActions] : [...receiver.continuationActions],
		createdAt: (evaluation ? donor.updatedAt : receiver.updatedAt) || timeStamp.toISOString(),
		fieldTemplateId: evaluation ? donor.fieldTemplateId : receiver.fieldTemplateId,
		formTemplateId: evaluation ? donor.formTemplateId : receiver.formTemplateId,
		group: evaluation ? donor.group : receiver.group,
		readGroups: evaluation ? [...donor.readGroups] : [...receiver.readGroups],
		instanceToContinueOn: (evaluation ? donor.instanceToContinueOn : receiver.instanceToContinueOn) || "",
		parentFormTemplateId: evaluation ? donor.parentFormTemplateId : receiver.parentFormTemplateId,
		updatedAt: (evaluation ? donor.updatedAt : receiver.updatedAt) || timeStamp.toISOString(),
	};
	return merged;
}

/**
 * Given the parent formTemplate id, deletes all continuationDatas by the formTemplateId
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formTemplateId The id of the formTemplate
 */
export async function deleteFieldContinuationDatasByFormTemplate(formTemplateId: string): Promise<void> {
	await db.fieldContinuationData.where("parentFormTemplateId").equals(formTemplateId).delete();
}

/**
 * Gets a Field Continuation Document
 * @param id of the continuation data to retrieve
 */
export async function getFieldContinuationDataDocument(id: string): Promise<FieldContinuationDataForResolver | undefined> {
	const retrieved = await db.fieldContinuationData.get(id);
	return retrieved;
}

/**
 * Given the formTemplateId, grabs all fieldContinuationDatas by it
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formTemplate The id of the parent formtemplate
 * @returns All fieldContinuationDatas that belong to the parent
 */
export async function getFieldContinuationDatasByFormTemplate(formTemplate: string) {
	const fieldContinuationDatas = await db.fieldContinuationData.where("parentFormTemplateId").equals(formTemplate).toArray();
	return fieldContinuationDatas;
}

/**
 * Bulk deletes field continuation datas from the offline db.
 * @param fieldContinuationDatas FieldContinuationDatas that need to be deleted.
 */
export async function bulkDeleteFieldContinuationDatas(fieldContinuationDataIds: string[]) {
	try {
		await db.fieldContinuationData.bulkDelete(fieldContinuationDataIds);
	} catch (err) {
		recordSentry({ error: err, msg: "Error batch deleting fieldTemplates in offline db" });
	}
}
